import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import { authAPI, User, getStoredToken, clearAuth, Role, Permission } from '../api/client';

/**
 * Permission definitions for each role
 */
const ROLE_PERMISSIONS: Record<Role, Permission[]> = {
  admin: [
    'sessions.view_all',
    'sessions.delete_any',
    'users.view',
    'users.create',
    'users.edit',
    'users.delete',
    'settings.view',
    'settings.edit',
    'storage.view',
    'storage.audit',
    'storage.reset',
  ],
  power_user: [
    'sessions.view_all',
    'users.view',
    'settings.view',
    'settings.edit',
    'storage.view',
    'storage.audit',
  ],
  user: [],
};

/**
 * Get permissions for a role
 */
export function getPermissions(role: Role): Permission[] {
  return ROLE_PERMISSIONS[role] ?? [];
}

/**
 * Check if a role has a specific permission
 */
export function hasPermission(role: Role | undefined, permission: Permission): boolean {
  if (!role) return false;
  return ROLE_PERMISSIONS[role]?.includes(permission) ?? false;
}

/**
 * Authentication context type
 */
export interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  permissions: Permission[];
  hasPermission: (permission: Permission) => boolean;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => Promise<void>;
  clearError: () => void;
}

/**
 * Auth context with undefined default (must be used within provider)
 */
const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Authentication Provider Component
 * Manages authentication state and provides auth methods
 */
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true); // Start as loading to check stored token
  const [error, setError] = useState<string | null>(null);

  /**
   * Validate stored token on mount
   */
  useEffect(() => {
    const validateToken = async () => {
      const storedToken = getStoredToken();

      if (!storedToken) {
        setIsLoading(false);
        return;
      }

      try {
        const { user: userData } = await authAPI.getMe();
        setUser(userData);
      } catch (err) {
        // Token is invalid or expired
        clearAuth();
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    validateToken();
  }, []);

  /**
   * Login with username and password
   */
  const login = useCallback(
    async (username: string, password: string): Promise<boolean> => {
      setIsLoading(true);
      setError(null);

      try {
        const { user: userData } = await authAPI.login(username, password);
        setUser(userData);
        return true;
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : 'Invalid credentials. Please try again.';

        // Check for specific API error responses
        if (typeof err === 'object' && err !== null && 'response' in err) {
          const response = (err as { response?: { data?: { error?: string } } })
            .response;
          if (response?.data?.error) {
            setError(response.data.error);
            return false;
          }
        }

        setError(errorMessage);
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  /**
   * Logout and clear session
   */
  const logout = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      await authAPI.logout();
    } catch (err) {
      // Log error but still clear local state
      console.error('Logout error:', err);
    } finally {
      setUser(null);
      clearAuth();
      setIsLoading(false);
    }
  }, []);

  /**
   * Clear error message
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * User's permissions based on role
   */
  const permissions = useMemo<Permission[]>(() => {
    if (!user?.role) return [];
    return getPermissions(user.role);
  }, [user?.role]);

  /**
   * Check if current user has a permission
   */
  const checkPermission = useCallback(
    (permission: Permission): boolean => {
      return hasPermission(user?.role, permission);
    },
    [user?.role],
  );

  /**
   * Memoized context value
   */
  const value = useMemo<AuthContextType>(
    () => ({
      user,
      isAuthenticated: !!user,
      isLoading,
      error,
      permissions,
      hasPermission: checkPermission,
      login,
      logout,
      clearError,
    }),
    [user, isLoading, error, permissions, checkPermission, login, logout, clearError],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Hook to access authentication context
 * @throws Error if used outside of AuthProvider
 */
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
};

/**
 * Hook to require authentication
 * Redirects to login if not authenticated
 */
export const useRequireAuth = (): AuthContextType => {
  const auth = useAuth();

  useEffect(() => {
    if (!auth.isLoading && !auth.isAuthenticated) {
      window.location.href = '/login';
    }
  }, [auth.isLoading, auth.isAuthenticated]);

  return auth;
};

/**
 * Hook to check if user has a specific permission
 */
export const usePermission = (permission: Permission): boolean => {
  const { hasPermission } = useAuth();
  return hasPermission(permission);
};

/**
 * Hook to check if user has any of the specified permissions
 */
export const useAnyPermission = (...permissions: Permission[]): boolean => {
  const { hasPermission } = useAuth();
  return permissions.some(hasPermission);
};

/**
 * Component to conditionally render based on permission
 */
export const PermissionGate: React.FC<{
  permission: Permission;
  children: React.ReactNode;
  fallback?: React.ReactNode;
}> = ({ permission, children, fallback = null }) => {
  const hasAccess = usePermission(permission);
  return <>{hasAccess ? children : fallback}</>;
};

/**
 * Component to conditionally render based on any of multiple permissions
 */
export const AnyPermissionGate: React.FC<{
  permissions: Permission[];
  children: React.ReactNode;
  fallback?: React.ReactNode;
}> = ({ permissions, children, fallback = null }) => {
  const hasAccess = useAnyPermission(...permissions);
  return <>{hasAccess ? children : fallback}</>;
};

/**
 * Get display label for a role
 */
export const getRoleLabel = (role: Role): string => {
  switch (role) {
    case 'admin':
      return 'Admin';
    case 'power_user':
      return 'Power User';
    case 'user':
      return 'User';
    default:
      return role;
  }
};

/**
 * Get color class for a role badge
 */
export const getRoleColor = (role: Role): string => {
  switch (role) {
    case 'admin':
      return 'role-admin';
    case 'power_user':
      return 'role-power-user';
    case 'user':
      return 'role-user';
    default:
      return '';
  }
};
